import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { EMAIL, PHONE_DISPLAY, PHONE_TEL } from "@/lib/contact";

const sections = [
  {
    title: "1. Titolare del trattamento",
    body: "Il titolare del trattamento dei dati personali è ANAS Milano Formazione, con sede in Via Mazzali 5, 20134 Milano (MI), raggiungibile all'indirizzo email formazione@anasmilano.com e al numero +39 351 393 7976.",
  },
  {
    title: "2. Dati raccolti",
    body: "Attraverso il modulo di richiesta informazioni raccogliamo: nome, cognome, numero di telefono, indirizzo email, modalità di frequenza preferita (in presenza o online) ed eventuali messaggi liberi che decidi di inviarci. Non raccogliamo dati particolari (c.d. sensibili) né dati di pagamento tramite il sito.",
  },
  {
    title: "3. Finalità e base giuridica",
    body: "I dati sono trattati esclusivamente per ricontattarti e fornirti le informazioni richieste sul Corso SAB (ex REC): calendario, modalità di iscrizione, pagamento e verifica dei requisiti. La base giuridica del trattamento è il consenso da te espresso tramite la spunta di accettazione nel modulo (art. 6, par. 1, lett. a del Reg. UE 2016/679 - GDPR).",
  },
  {
    title: "4. Modalità del trattamento",
    body: "Il trattamento avviene con strumenti elettronici e misure di sicurezza adeguate. I dati non sono diffusi, non sono ceduti a terzi per finalità di marketing e non sono oggetto di processi decisionali automatizzati o profilazione. Per ogni richiesta inviata ricevi un'email di conferma automatica all'indirizzo indicato.",
  },
  {
    title: "5. Conservazione dei dati",
    body: "I dati sono conservati per il tempo necessario a gestire la tua richiesta e comunque non oltre 24 mesi dall'ultimo contatto, salvo tua diversa richiesta di cancellazione anticipata.",
  },
  {
    title: "6. I tuoi diritti",
    body: "In qualsiasi momento puoi esercitare i diritti previsti dagli artt. 15-22 del GDPR: accesso, rettifica, cancellazione, limitazione, opposizione al trattamento e revoca del consenso. Puoi farlo scrivendo a formazione@anasmilano.com o chiamando il +39 351 393 7976. Hai inoltre diritto di proporre reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it).",
  },
  {
    title: "7. Cookie",
    body: "Questo sito non utilizza cookie di profilazione né strumenti di tracciamento di terze parti. Sono presenti unicamente risorse tecniche necessarie al funzionamento delle pagine.",
  },
];

export default function Privacy() {
  return (
    <div className="min-h-screen bg-paper font-body text-ink antialiased" data-testid="privacy-page">
      <header className="border-b border-line bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/logo-anas.png" alt="Logo ANAS Milano Formazione" className="h-10 w-10 rounded-full object-cover" data-testid="privacy-logo" />
            <span className="font-heading text-sm font-bold tracking-tight text-navy">ANAS Milano Formazione</span>
          </div>
          <Link
            to="/"
            className="flex items-center gap-2 border border-navy px-4 py-2 font-heading text-xs font-bold uppercase tracking-wider text-navy transition-colors hover:bg-navy hover:text-white"
            data-testid="privacy-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Torna al sito
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-14 sm:px-6 md:py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <p className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-brand-amber" data-testid="privacy-eyebrow">
            Reg. UE 2016/679 - GDPR
          </p>
          <h1 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl" data-testid="privacy-title">
            Informativa sulla privacy
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg" data-testid="privacy-intro">
            Questa informativa descrive come ANAS Milano Formazione tratta i dati personali che ci
            fornisci tramite il modulo di richiesta informazioni sul Corso SAB (ex REC).
          </p>
        </motion.div>

        <div className="mt-12 space-y-6">
          {sections.map((s, i) => (
            <motion.section
              key={s.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
              className="border border-line bg-white p-6 sm:p-8"
              data-testid={`privacy-section-${i + 1}`}
            >
              <h2 className="font-heading text-lg font-bold tracking-tight text-navy sm:text-xl">{s.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">{s.body}</p>
            </motion.section>
          ))}
        </div>

        <p className="mt-10 text-sm text-slate-500" data-testid="privacy-contacts">
          Per qualsiasi domanda su questa informativa:{" "}
          <a href={`mailto:${EMAIL}`} className="font-semibold text-navy underline underline-offset-2">{EMAIL}</a>
          {" · "}
          <a href={PHONE_TEL} className="font-semibold text-navy underline underline-offset-2">{PHONE_DISPLAY}</a>
        </p>
      </main>

      <footer className="border-t border-white/10 bg-navy-deep py-8 text-center text-xs text-slate-500" data-testid="privacy-footer">
        © 2026 ANAS Milano Formazione — Via Mazzali 5, Milano · Tutti i diritti riservati.
      </footer>
    </div>
  );
}
